/* eslint-disable no-console */
/* eslint-disable max-len */
import {conditionType, IField} from '../interface/inputType';

type GetFieldValueFunction = (fieldName: string | string[]) => any;

/**
 * Checks if a field should be visible based on conditional visibility rules
 * @param field - The field configuration object
 * @param getFieldValue - Function to get field values from the form
 * @returns true if field should be visible, false otherwise
 */
export const isFieldVisible = (
  field: IField,
  getFieldValue: GetFieldValueFunction
): boolean => {
  if (!field?.conditionalVisibility) return true;

  const {
    dependsOn,
    showWhen,
    condition = conditionType.EQUALS,
    fieldName,
    relatedTableKey
  } = field?.conditionalVisibility;

  let dependentValue = getFieldValue(dependsOn);
  if (relatedTableKey) {
    const dependsOnTableKeyValue = getFieldValue(relatedTableKey) ?? [];
    const tableData = Array.isArray(dependsOnTableKeyValue)
      ? dependsOnTableKeyValue
      : [dependsOnTableKeyValue];
    dependentValue = tableData[0]?.[dependsOn];
  }

  if (dependentValue === undefined || dependentValue === null) return false;

  const showWhenArray = Array?.isArray(showWhen) ? showWhen : [showWhen ?? ''];

  if (Array?.isArray(dependentValue) && dependentValue.length > 0) {
    const hasMatchingValue = dependentValue?.some((item: any) => {
      if (typeof item === 'object' && item !== null) {
        // Use the provided fieldName or extract from dependsOn
        const targetFieldName = fieldName ?? dependsOn?.split('.').pop();
        if (!targetFieldName) return false;
        const itemValue = item[targetFieldName];

        // Handle both array (MULTISELECT) and single values
        if (Array.isArray(itemValue)) {
          // If itemValue is an array, check if any value matches showWhen
          return itemValue.some((val: any) => showWhenArray?.includes(val));
        } else {
          // If itemValue is a single value, check if it's in showWhen
          return showWhenArray?.includes(itemValue);
        }
      }

      return false;
    });

    switch (condition) {
      case conditionType.EQUALS:
        return hasMatchingValue;
      case conditionType.NOT:
        return !hasMatchingValue;
      case conditionType.INCLUDES:
        return hasMatchingValue;
      case conditionType.NOT_INCLUDES:
        return !hasMatchingValue;
      default:
        return hasMatchingValue;
    }
  }

  switch (condition) {
    case conditionType.EQUALS:
      return showWhenArray?.includes(dependentValue);
    case conditionType.NOT:
      return !showWhenArray?.includes(dependentValue);
    case conditionType.INCLUDES:
      return showWhenArray?.some((value) =>
        Array.isArray(dependentValue)
          ? dependentValue?.includes(value)
          : String(dependentValue)?.includes(String(value))
      );
    case conditionType.NOT_INCLUDES:
      return !showWhenArray?.some((value) =>
        Array.isArray(dependentValue)
          ? dependentValue?.includes(value)
          : String(dependentValue)?.includes(String(value))
      );
    case conditionType.GREATER_THAN: {
      const numValue = Number(dependentValue);
      const threshold = field.conditionalVisibility?.numericValue ?? 0;

      return !isNaN(numValue) && numValue > threshold;
    }
    case conditionType.LESS_THAN: {
      const numValue = Number(dependentValue);
      const threshold = field.conditionalVisibility?.numericValue ?? 0;

      return !isNaN(numValue) && numValue < threshold;
    }
    default:
      return showWhenArray.includes(dependentValue);
  }
};

/**
 * Checks if a field should be disabled based on disabled rules
 * @param formField - The field configuration object
 * @param getFieldValue - Function to get field values from the form
 * @param rowId - Optional rowId to check against rowIdPattern
 * @returns true if field should be disabled, false otherwise
 */
export const isFieldDisabled = (
  formField: any,
  getFieldValue: GetFieldValueFunction,
  rowId?: string,
  dependentValueFromMainForm?: string
): boolean => {
  if (formField.disabled) return true;

  if (formField.disabledOn) {
    const {fieldName, condition, values, rowIdPattern} = formField.disabledOn;

    // Check rowIdPattern if provided
    if (rowIdPattern && rowId) {
      const patterns = Array.isArray(rowIdPattern)
        ? rowIdPattern
        : [rowIdPattern];
      const rowMatchesPattern = patterns.some((pattern) =>
        rowId.startsWith(pattern)
      );
      // If row doesn't match any pattern, disable the field
      if (!rowMatchesPattern) {
        return true;
      }
    }

    const dependentValue =
      getFieldValue(fieldName) ?? dependentValueFromMainForm;

    switch (condition) {
      case conditionType.EQUALS:
        return values.includes(dependentValue);
      case conditionType.NOT:
        return (
          dependentValue === undefined ||
          dependentValue === null ||
          dependentValue === '' ||
          !values.includes(dependentValue)
        );
      case conditionType.INCLUDES:
        // Disable when dependentValue includes any of the values
        if (Array.isArray(dependentValue)) {
          // For arrays (MULTISELECT), check if any value from values array is in dependentValue
          return values?.some(
            (value: string) => dependentValue?.includes(value)
          );
        }
        // For strings, check if dependentValue includes any of the values

        return values?.some(
          (value: string) => String(dependentValue)?.includes(String(value))
        );
      case conditionType.NOT_INCLUDES:
        // Disable when dependentValue does NOT include any of the values
        if (Array.isArray(dependentValue)) {
          // For arrays (MULTISELECT), disable when dependentValue does NOT include any of the values

          return !values?.some(
            (value: string) => dependentValue?.includes(value)
          );
        }
        // For strings, disable when dependentValue does NOT include any of the values

        return !values?.some(
          (value: string) => String(dependentValue)?.includes(String(value))
        );
      case conditionType.EMPTY:
        return (
          dependentValue === undefined ||
          dependentValue === null ||
          dependentValue === '' ||
          (typeof dependentValue === 'string' && dependentValue.trim() === '')
        );
      case conditionType.NOT_EMPTY:
        return (
          dependentValue !== undefined &&
          dependentValue !== null &&
          dependentValue !== '' &&
          !(typeof dependentValue === 'string' && dependentValue.trim() === '')
        );
      case conditionType.GREATER_THAN:
        const numValue = Number(dependentValue);
        const threshold = formField.disabledOn?.numericValue ?? 0;

        return isNaN(numValue) || numValue <= threshold;
      default:
        return false;
    }
  }

  return false;
};

/**
 * Checks if a field is required based on required rules
 * @param formField - The field configuration object
 * @param getFieldValue - Function to get field values from the form
 * @param rowId - Optional rowId to check against rowIdPattern
 * @returns true if field is required, false otherwise
 */
export const isFieldRequired = (
  formField: any,
  getFieldValue: GetFieldValueFunction,
  rowId?: string
): boolean => {
  if (formField.isRequired !== undefined) {
    return formField.isRequired;
  }

  if (formField.requiredOn) {
    const {
      fieldName,
      condition,
      values = [],
      required,
      rowIdPattern
    } = formField.requiredOn;
    if (required !== true) return false;

    // Check rowIdPattern if provided - field is only required if row matches any pattern
    if (rowIdPattern && rowId) {
      const patterns = Array.isArray(rowIdPattern)
        ? rowIdPattern
        : [rowIdPattern];
      const rowMatchesPattern = patterns.some((pattern) =>
        rowId.startsWith(pattern)
      );
      if (!rowMatchesPattern) {
        return false;
      }
    }

    const dependentValue = getFieldValue(fieldName);

    switch (condition) {
      case conditionType.EQUALS:
        return values.includes(dependentValue);
      case conditionType.INCLUDES:
        if (Array.isArray(dependentValue)) {
          return dependentValue.some((value) => values.includes(value));
        }

        return values.includes(dependentValue);
      case conditionType.LESS_THAN: {
        const numValueLessThan = Number(dependentValue);
        const thresholdLessThan = formField.requiredOn?.numericValue ?? 0;

        return !isNaN(numValueLessThan) && numValueLessThan < thresholdLessThan;
      }
      case conditionType.GREATER_THAN: {
        const numValueGreaterThan = Number(dependentValue);
        const thresholdGreaterThan = formField.requiredOn?.numericValue ?? 0;

        return (
          !isNaN(numValueGreaterThan) &&
          numValueGreaterThan > thresholdGreaterThan
        );
      }
      default:
        return false;
    }
  }

  return false;
};

/**
 * Checks if a question/item should be visible based on conditional visibility rules
 * @param item - The question/item configuration object with conditionalVisibility
 * @param getFieldValue - Function to get field values from the form
 * @returns true if question should be visible, false otherwise
 */
export const isQuestionVisible = (
  item: any,
  getFieldValue: GetFieldValueFunction
): boolean => {
  if (!item?.conditionalVisibility) return true;

  const {
    dependsOn,
    showWhen,
    condition = conditionType.EQUALS,
    fieldName,
    relatedTableKey
  } = item.conditionalVisibility;

  let dependentValue = getFieldValue(dependsOn);
  if (relatedTableKey) {
    const dependsOnTableKeyValue = getFieldValue(relatedTableKey) ?? [];
    const tableData = Array.isArray(dependsOnTableKeyValue)
      ? dependsOnTableKeyValue
      : [dependsOnTableKeyValue];
    dependentValue = tableData[0]?.[dependsOn];
  }

  if (dependentValue === undefined || dependentValue === null) return false;

  const showWhenArray = Array?.isArray(showWhen) ? showWhen : [showWhen ?? ''];

  if (Array?.isArray(dependentValue) && dependentValue.length > 0) {
    const hasMatchingValue = dependentValue?.some((item: any) => {
      if (typeof item === 'object' && item !== null) {
        // Use the provided fieldName or extract from dependsOn
        const targetFieldName = fieldName ?? dependsOn?.split('.').pop();
        if (!targetFieldName) return false;
        const itemValue = item[targetFieldName];

        // Handle both array (MULTISELECT) and single values
        if (Array.isArray(itemValue)) {
          // If itemValue is an array, check if any value matches showWhen
          return itemValue.some((val: any) => showWhenArray?.includes(val));
        } else {
          // If itemValue is a single value, check if it's in showWhen
          return showWhenArray?.includes(itemValue);
        }
      }

      return false;
    });

    switch (condition) {
      case conditionType.EQUALS:
        return hasMatchingValue;
      case conditionType.NOT:
        return !hasMatchingValue;
      case conditionType.INCLUDES:
        return hasMatchingValue;
      case conditionType.NOT_INCLUDES:
        return !hasMatchingValue;
      default:
        return hasMatchingValue;
    }
  }

  switch (condition) {
    case conditionType.EQUALS:
      return showWhenArray?.includes(dependentValue);
    case conditionType.NOT:
      return !showWhenArray?.includes(dependentValue);
    case conditionType.INCLUDES:
      return showWhenArray?.some((value) =>
        Array.isArray(dependentValue)
          ? dependentValue?.includes(value)
          : String(dependentValue)?.includes(String(value))
      );
    case conditionType.NOT_INCLUDES:
      return !showWhenArray?.some((value) =>
        Array.isArray(dependentValue)
          ? dependentValue?.includes(value)
          : String(dependentValue)?.includes(String(value))
      );
    case conditionType.GREATER_THAN: {
      const numValue = Number(dependentValue);
      const threshold = item.conditionalVisibility?.numericValue ?? 0;

      return !isNaN(numValue) && numValue > threshold;
    }
    case conditionType.LESS_THAN: {
      const numValue = Number(dependentValue);
      const threshold = item.conditionalVisibility?.numericValue ?? 0;

      return !isNaN(numValue) && numValue < threshold;
    }
    default:
      return showWhenArray.includes(dependentValue);
  }
};
